<template>
  <div class="loading-container">
    <div class="loading-content">
      
      <!-- Status information -->
      <div class="status-info">
        <h3 class="status-title">System is initializing...</h3>
        
        <!-- Connection status steps -->
        <div class="status-steps">
          <div class="step" :class="{ active: authReceived, completed: authReceived }">
            <div class="step-icon">
              <div v-if="authReceived" class="icon-check">✓</div>
              <div v-else class="icon-wait">🔐</div>
            </div>
            <div class="step-content">
              <div class="step-title">Authentication</div>
              <div class="step-description">
                {{ authReceived ? 'Authentication successful' : 'Checking authentication...' }}
              </div>
            </div>
          </div>
          
          <div class="step" :class="{ active: mqttStore.hasAuthData && !mqttStore.isConnected, completed: mqttStore.isConnected }">
            <div class="step-icon">
              <div v-if="mqttStore.isConnected" class="icon-check">✓</div>
              <div v-else-if="mqttStore.isConnecting" class="icon-spin">🌐</div>
              <div v-else class="icon-wait">⏳</div>
            </div>
            <div class="step-content">
              <div class="step-title">Connection</div>
              <div class="step-description">
                {{ mqttStore.connectionStatus }}
              </div>
            </div>
          </div>
          
          <div class="step" :class="{ 
            active: mqttStore.isConnected && !configLoaded && !configTimeout, 
            completed: configLoaded,
            error: configTimeout && !configLoaded
          }">
            <div class="step-icon">
              <div v-if="configLoaded" class="icon-check">✓</div>
              <div v-else-if="configTimeout" class="icon-error">⚠️</div>
              <div v-else class="icon-wait">⚙️</div>
            </div>
            <div class="step-content">
              <div class="step-title">System Configuration</div>
              <div class="step-description">
                <span v-if="configLoaded">Configuration loaded</span>
                <span v-else-if="configTimeout" class="timeout-message">Device not responding!</span>
                <span v-else>Loading system configuration...</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Error display -->
        <div v-if="mqttStore.lastError" class="error-message">
          <div class="error-icon">⚠️</div>
          <span>{{ mqttStore.lastError }}</span>
        </div>
        
        <!-- Debug info (only in development) -->
        <div v-if="isDev" class="debug-info">
          <details>
            <summary>Debug Information</summary>
            <div class="debug-details">
              <div><strong>Auth Data:</strong> {{ mqttStore.hasAuthData ? '✅' : '❌' }}</div>
              <div><strong>Device ID:</strong> {{ mqttStore.deviceId || 'N/A' }}</div>
              <div><strong>Session ID:</strong> {{ mqttStore.sessionId || 'N/A' }}</div>
              <div><strong>Connection Status:</strong> {{ mqttStore.connectionStatus }}</div>
              <div><strong>Is Connecting:</strong> {{ mqttStore.isConnecting ? 'Yes' : 'No' }}</div>
              <div><strong>Is Connected:</strong> {{ mqttStore.isConnected ? 'Yes' : 'No' }}</div>
            </div>
          </details>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useMqttStore } from '@/stores/mqtt'
import { config } from '@/composables/useMqttConnection'

const mqttStore = useMqttStore()
const authReceived = ref(false)
const isDev = import.meta.env.DEV
const configTimeout = ref(false)
const configTimeoutId = ref<number | null>(null)

// Watch for auth data reception
const configLoaded = computed(() => {
  return config.value && Object.keys(config.value).length > 0
})

// Listen for auth data reception
onMounted(() => {
  // Check if we already have auth data
  if (mqttStore.hasAuthData) {
    authReceived.value = true
  }
  
  // Watch for auth data changes
  const unwatch = mqttStore.$subscribe((mutation, state) => {
    if (state.hasAuthData && !authReceived.value) {
      authReceived.value = true
    }
  })
  
  // Set up timeout for configuration loading
  const startConfigTimeout = () => {
    if (mqttStore.isConnected && !configLoaded.value) {
      configTimeoutId.value = window.setTimeout(() => {
        configTimeout.value = true
      }, 3000) // 3 seconds timeout
    }
  }
  
  // Start timeout when MQTT connects
  const unwatchMqtt = mqttStore.$subscribe((mutation, state) => {
    if (state.isConnected && !configLoaded.value && !configTimeout.value) {
      startConfigTimeout()
    }
  })
  
  // Cleanup watchers and timeout when component unmounts
  return () => {
    unwatch()
    unwatchMqtt()
    if (configTimeoutId.value) {
      clearTimeout(configTimeoutId.value)
    }
  }
})
</script>

<style scoped>
.loading-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 9999;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 500px;
  width: 90%;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}


.status-info {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.status-title {
  margin: 0 0 1.5rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  text-align: center;
  width: 100%;
}

.status-steps {
  width: 100%;
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
}

.step {
  display: flex;
  align-items: flex-start;
  padding: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.3s ease;
  width: 100%;
  min-height: 60px;
  box-sizing: border-box;
  flex: 1;
}

.step:last-child {
  border-bottom: none;
}

.step.active {
  background-color: #f8f9ff;
  border-radius: 8px;
  padding: 0.75rem;
  margin: 0.25rem 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 100%;
  box-sizing: border-box;
  flex: 1;
}

.step.completed {
  background-color: #f8fff8;
  border-radius: 8px;
  padding: 0.75rem;
  margin: 0.25rem 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  opacity: 0.9;
  width: 100%;
  box-sizing: border-box;
  flex: 1;
}

.step.error {
  background-color: #fff8f8;
  border-radius: 8px;
  padding: 0.75rem;
  margin: 0.25rem 0;
  box-shadow: 0 2px 4px rgba(255, 0, 0, 0.1);
  border: 1px solid #ffebee;
  width: 100%;
  box-sizing: border-box;
  flex: 1;
}

.step-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  border-radius: 50%;
  background-color: #f8f9fa;
  color: #6c757d;
  font-size: 1rem;
  transition: all 0.3s ease;
  border: 2px solid #e9ecef;
}

.step.active .step-icon {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
  box-shadow: 0 0 0 4px rgba(0, 123, 255, 0.2);
}

.step.completed .step-icon {
  background-color: #28a745;
  color: white;
  border-color: #28a745;
}

.step.error .step-icon {
  background-color: #dc3545;
  color: white;
  border-color: #dc3545;
}

.icon-check {
  font-weight: bold;
  font-size: 1.1rem;
}

.icon-wait {
  font-size: 1rem;
}

.icon-spin {
  font-size: 1rem;
  animation: pulse 1.5s ease-in-out infinite;
}

.icon-error {
  font-size: 1rem;
}

.timeout-message {
  color: #dc3545;
  font-weight: 500;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.step-content {
  flex: 1;
  min-width: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.step-title {
  font-weight: 600;
  color: #333;
  margin-bottom: 0.25rem;
}

.step-description {
  font-size: 0.875rem;
  color: #666;
  line-height: 1.4;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
}

.error-message {
  display: flex;
  align-items: center;
  padding: 1rem;
  background: linear-gradient(135deg, #ff6b6b, #ee5a52);
  color: white;
  border-radius: 12px;
  margin-top: 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
}

.error-icon {
  margin-right: 0.75rem;
  font-size: 1.1rem;
}

.debug-info {
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: #f8f9fa;
  border-radius: 6px;
  font-size: 0.75rem;
}

.debug-info summary {
  cursor: pointer;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
}

.debug-details {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.25rem;
  margin-top: 0.5rem;
}

.debug-details div {
  padding: 0.25rem 0;
}


/* Responsive design */
@media (max-width: 480px) {
  .loading-content {
    padding: 1.5rem;
    margin: 1rem;
  }
  
  .debug-details {
    grid-template-columns: 1fr;
  }
}
</style>
